import React from 'react';
import { Link } from 'react-router-dom';
import '../ComponentsStyling/Footer.css';
import { FaInstagram, FaLinkedinIn, FaFacebook, FaMapMarkerAlt, FaEnvelope, FaChevronRight, FaYoutube } from 'react-icons/fa';
import { CHAPTER_SOCIAL_LINKS } from '../socialLinks';

const brandingImage = (fileName) => `${process.env.PUBLIC_URL}/images/webp/branding/${fileName}`;

const socialLinks = [
    { href: CHAPTER_SOCIAL_LINKS.instagram, iconClassName: 'social-icon instagram', Icon: FaInstagram, label: 'Instagram' },
    { href: CHAPTER_SOCIAL_LINKS.linkedin, iconClassName: 'social-icon linkedin', Icon: FaLinkedinIn, label: 'LinkedIn' },
    { href: CHAPTER_SOCIAL_LINKS.facebook, iconClassName: 'social-icon facebook', Icon: FaFacebook, label: 'Facebook' },
    { href: '/youtube', iconClassName: 'social-icon youtube', Icon: FaYoutube, label: 'YouTube', internal: true },
];

const ieeeAdminLinks = [
    { href: 'https://www.ieee.org/security-privacy.html', label: 'IEEE Privacy Statement', external: true },
    { href: 'https://www.ieee.org/about/corporate/governance/p9-26.html', label: 'IEEE Non-Discrimination Policy', external: true },
    { href: 'https://www.ieee.org/accessibility-statement.html', label: 'IEEE Accessibility Statement', external: true },
    { href: 'https://supportcenter.ieee.org/', label: 'IEEE Contact Center', external: true },
    { href: '/privacy-policy', label: 'Chapter Privacy Policy', external: false },
];

const ieeeResourceLinks = [
    { href: 'https://www.ieee.org/', label: 'IEEE.org' },
    { href: 'https://ieeexplore.ieee.org/', label: 'IEEE Xplore' },
    { href: 'https://spectrum.ieee.org/', label: 'IEEE Spectrum' },
    { href: 'https://www.ieee.org/membership/join/index.html', label: 'Join IEEE' },
];

const Footer = ({ isDarkMode }) => {
    return (
        <footer id="contact" className={`footer ${isDarkMode ? 'footer-dark' : 'footer-light'}`}>
            <div className="footer-container">
                <section className="footer-section brand-section" aria-label="Organization overview">
                    <div className="logo-container">
                        <img
                            src={isDarkMode ? brandingImage('IEEE-Logo-base-png.webp') : brandingImage('IEEE-Logo-base-black-png.webp')}
                            alt="IEEE CIS UJ Logo"
                            className="footer-logo"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <div className="brand-content">
                        <h3>IEEE CIS UJ</h3>
                        <p>
                            We are a student-led chapter of the IEEE Computational Intelligence Society, dedicated to advancing AI
                            research, education, and innovation at the University of Jordan.
                        </p>
                    </div>
                </section>

                <nav className="footer-section nav-section" aria-label="Main pages">
                    <h4>Site Pages</h4>
                    <ul className="footer-nav">
                        <li><Link to="/"><FaChevronRight className="nav-icon" /> Home</Link></li>
                        <li><Link to="/partners"><FaChevronRight className="nav-icon" /> Partners</Link></li>
                        <li><Link to="/teams"><FaChevronRight className="nav-icon" /> Teams</Link></li>
                        <li><Link to="/events"><FaChevronRight className="nav-icon" /> Events</Link></li>
                        <li><Link to="/join"><FaChevronRight className="nav-icon" /> Join</Link></li>
                    </ul>
                </nav>

                <nav className="footer-section discover-section" aria-label="Extra pages">
                    <h4>Policies & Admin</h4>
                    <ul className="footer-nav">
                        {ieeeAdminLinks.map(({ href, label, external }) => (
                            <li key={label}>
                                {external ? (
                                    <a href={href} target="_blank" rel="noopener noreferrer"><FaChevronRight className="nav-icon" /> {label}</a>
                                ) : (
                                    <Link to={href}><FaChevronRight className="nav-icon" /> {label}</Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav className="footer-section ieee-section" aria-label="IEEE official resources">
                    <h4>IEEE Resources</h4>
                    <ul className="footer-nav">
                        {ieeeResourceLinks.map(({ href, label }) => (
                            <li key={label}>
                                <a href={href} target="_blank" rel="noopener noreferrer"><FaChevronRight className="nav-icon" /> {label}</a>
                            </li>
                        ))}
                        <li><Link to="/all-events"><FaChevronRight className="nav-icon" /> Event Archive</Link></li>
                    </ul>
                </nav>

                <section className="footer-section contact-section" aria-label="Contact information">
                    <h4>Get In Touch</h4>
                    <div className="contact-info">
                        <div className="contact-item">
                            <FaMapMarkerAlt className="contact-icon" />
                            <a href="https://maps.google.com/?q=IEEE+CIS+AI+JU" target="_blank" rel="noopener noreferrer">
                                IEEE CIS, University of Jordan
                            </a>
                        </div>
                        <div className="contact-item">
                            <FaEnvelope className="contact-icon" />
                            <a href="mailto:ieeecis2026@gmail.com">ieeecis2026@gmail.com</a>
                        </div>
                    </div>

                    <div className="social-links">
                        {socialLinks.map(({ href, iconClassName, Icon, label, internal }) => (
                            internal ? (
                                <Link key={label} to={href} aria-label={label}>
                                    <div className={iconClassName}><Icon /></div>
                                </Link>
                            ) : (
                                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                                    <div className={iconClassName}><Icon /></div>
                                </a>
                            )
                        ))}
                    </div>
                </section>

            </div>

            <div className="footer-divider" />

            <div className="footer-bottom">
                <div className="copyright">
                    <p>IEEE Computational Intelligence Society Student Branch at the University of Jordan © {new Date().getFullYear()}.</p>
                    <p className="footer-legal">IEEE and its logo are trademarks owned by The Institute of Electrical and Electronics Engineers, Incorporated. This website is maintained by IEEE CIS UJ as the chapter digital portal and references official IEEE administrative resources for accessibility, privacy, and support.</p>
                </div>
                <div className="bottom-social-links">
                    {socialLinks.map(({ href, Icon, label, internal }) => (
                        internal ? (
                            <Link key={label} to={href} aria-label={label}>
                                <Icon className="bottom-icon" />
                            </Link>
                        ) : (
                            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                                <Icon className="bottom-icon" />
                            </a>
                        )
                    ))}
                </div>
            </div>
        </footer>
    );
};

export default Footer;
